import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FaArrowLeft, FaInfoCircle } from "react-icons/fa";
import { BsCloudUpload, BsFileEarmarkPdf } from "react-icons/bs";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField } from "../../../Common/FormComponents";
import {
  cancelInvoiceApi,
  getContractsApi,
  getInvoiceApi,
  getInvoiceDefaultsApi,
  saveInvoiceApi,
} from "../../../../Api/ClientWork/ClientWork";
import "../../AddContractorsAndVendors.css";

// The contract-driven part of a bill; shared by the first load and by changing the linked contract.
const contractFields = (contract) => ({
  ContractId: contract.Id,
  ContractNo: contract.ContractNo,
  ClientId: contract.ClientId,
  ClientName: contract.ClientName,
  ClientNTN: contract.ClientNTN || "",
  ProjectName: contract.ProjectName,
  OriginalContractValue: contract.ContractValue || 0,
  RetentionRate: contract.RetentionPercentage || 5.0,
  IncomeTaxRate: contract.IncomeTaxWhtRate || 2.0,
});

/**
 * InvoiceForm Screen (Create / Edit Client Running Bill / IPC)
 * Designed after the standard ERP voucher layouts and client bill valuation lifecycle.
 */
const InvoiceForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);
  const defaultsRequest = useRef(0);

  const [isLoading, setIsLoading] = useState(false);
  const [contractsList, setContractsList] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    BillNumber: "",
    ContractId: "",
    ContractNo: "",
    ClientId: "",
    ClientName: "",
    ClientNTN: "",
    ProjectName: "",
    OriginalContractValue: 0,
    BillingDate: new Date().toISOString().split("T")[0],
    PeriodFrom: "",
    PeriodTo: "",
    CumulativePercentage: 0,
    CumulativeGross: 0,
    PreviousGross: 0,
    RetentionRate: 5.0,
    RetentionAmount: 0,
    AdvanceAmortization: 0,
    IncomeTaxRate: 2.0,
    IncomeTaxAmount: 0,
    SalesTaxRate: 0.0,
    SalesTaxAmount: 0,
    SalesTaxChargedRate: 0.0,
    SalesTaxChargedAmount: 0,
    OtherAdjustments: 0,
    AdjustmentNotes: "",
    ProgressCertificateFile: "",
    Status: "Draft",
  });

  // Load contracts and record on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const contractsResult = await getContractsApi();
      if (cancelled) {
        return;
      }
      const contracts =
        contractsResult?.data?.IsSuccess === true ? contractsResult.data.Data || [] : [];
      setContractsList(contracts);

      if (isEdit) {
        const result = await getInvoiceApi(id);
        if (cancelled) {
          return;
        }
        const existing = result?.data?.IsSuccess === true ? result.data.Data : null;
        if (existing) {
          const total = Number(existing.OriginalContractValue) || 0;
          const gross = Number(existing.CumulativeGross) || 0;
          const pct =
            existing.CumulativePercentage !== undefined
              ? existing.CumulativePercentage
              : total > 0
                ? parseFloat(((gross / total) * 100).toFixed(2))
                : 0;
          setFormData({ ...existing, CumulativePercentage: pct });
        } else {
          toast.error("Running Bill / Invoice record not found");
          history.push("/client-work/invoices");
        }
      } else if (contracts[0]) {
        // New bill: start from the first contract; the server supplies PreviousGross (the bill number comes on save).
        const defaults = await getInvoiceDefaultsApi(contracts[0].Id);
        if (cancelled) {
          return;
        }
        setFormData((prev) => ({
          ...prev,
          ...contractFields(contracts[0]),
          PreviousGross:
            defaults?.data?.IsSuccess === true
              ? defaults.data.Data.PreviousGross
              : prev.PreviousGross,
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, history]);

  // A saved bill other than a draft is read-only; the server refuses edits anyway.
  const isEditable = !isEdit || formData.IsEditable !== false;

  // Handle contract selection change
  const handleContractChange = async (e) => {
    const selectedContractNo = e.target.value;
    const contract = contractsList.find((c) => c.ContractNo === selectedContractNo);
    const requestNo = ++defaultsRequest.current; // a slower answer for an earlier pick must not win
    if (contract) {
      const contractVal = Number(contract.ContractValue) || 0;
      setFormData((prev) => {
        const pct = parseFloat(prev.CumulativePercentage);
        const gross =
          !isNaN(pct) && pct > 0 && contractVal > 0
            ? Math.round((pct / 100) * contractVal)
            : prev.CumulativeGross;
        return {
          ...prev,
          ...contractFields(contract),
          CumulativeGross: gross,
          CumulativePercentage:
            contractVal > 0 && gross > 0
              ? parseFloat(((gross / contractVal) * 100).toFixed(2))
              : prev.CumulativePercentage,
        };
      });
      const defaults = await getInvoiceDefaultsApi(contract.Id);
      if (requestNo === defaultsRequest.current && defaults?.data?.IsSuccess === true) {
        setFormData((prev) => ({ ...prev, PreviousGross: defaults.data.Data.PreviousGross }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        ContractId: "",
        ContractNo: "",
        ClientId: "",
        ClientName: "",
        ClientNTN: "",
        ProjectName: "",
        OriginalContractValue: 0,
        PreviousGross: 0,
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (name, value) => {
    const numeric = parseFloat(value) || 0;
    setFormData((prev) => ({
      ...prev,
      [name]: numeric,
    }));
  };

  // Cumulative work done as a percentage of the contract value drives the gross, and the other way round.
  const handleCumulativePercentageChange = (value) => {
    const total = Number(formData.OriginalContractValue) || 0;
    if (value === "") {
      setFormData((prev) => ({ ...prev, CumulativePercentage: "", CumulativeGross: 0 }));
      return;
    }

    let pct = parseFloat(value);
    if (isNaN(pct)) {
      setFormData((prev) => ({ ...prev, CumulativePercentage: value }));
      return;
    }

    // Percentage cannot be greater than 100 or less than 0
    if (pct > 100) {
      pct = 100;
      value = 100;
      toast.warning("Cumulative Percentage cannot be greater than 100%");
    } else if (pct < 0) {
      pct = 0;
      value = 0;
    }

    const calculatedGross = total > 0 ? Math.round((pct / 100) * total) : 0;
    setFormData((prev) => ({
      ...prev,
      CumulativePercentage: value,
      CumulativeGross: calculatedGross,
    }));
  };

  const handleCumulativeGrossChange = (value) => {
    const total = Number(formData.OriginalContractValue) || 0;
    if (value === "") {
      setFormData((prev) => ({ ...prev, CumulativeGross: "", CumulativePercentage: "" }));
      return;
    }

    let gross = parseFloat(value);
    if (isNaN(gross)) {
      setFormData((prev) => ({ ...prev, CumulativeGross: value }));
      return;
    }

    if (gross < 0) {
      gross = 0;
      value = 0;
    }

    // Cumulative Gross amount cannot be greater than Total Contract Value
    if (total > 0 && gross > total) {
      gross = total;
      value = total;
      toast.warning(
        `Cumulative Gross amount cannot exceed Total Contract Value (PKR ${total.toLocaleString()})`,
      );
    }

    const calculatedPct = total > 0 ? parseFloat(((gross / total) * 100).toFixed(2)) : "";
    setFormData((prev) => ({
      ...prev,
      CumulativeGross: value,
      CumulativePercentage:
        calculatedPct !== "" && Number(calculatedPct) > 100 ? 100 : calculatedPct,
    }));
  };

  // Dynamic Gross Calculation
  const currentGrossClaimed = useMemo(() => {
    const cumulative = Number(formData.CumulativeGross) || 0;
    const previous = Number(formData.PreviouslyBilled || formData.PreviousGross) || 0;
    return Math.max(0, cumulative - previous);
  }, [formData.CumulativeGross, formData.PreviouslyBilled, formData.PreviousGross]);

  // Sync automatic retention and WHT when gross or rates change
  useEffect(() => {
    const retention = Math.round(
      (currentGrossClaimed * (Number(formData.RetentionRate) || 0)) / 100,
    );
    const itWht = Math.round((currentGrossClaimed * (Number(formData.IncomeTaxRate) || 0)) / 100);
    const stWht = Math.round((currentGrossClaimed * (Number(formData.SalesTaxRate) || 0)) / 100);
    const stCharged = Math.round(
      (currentGrossClaimed * (Number(formData.SalesTaxChargedRate) || 0)) / 100,
    );

    setFormData((prev) => ({
      ...prev,
      RetentionAmount: retention,
      IncomeTaxAmount: itWht,
      SalesTaxAmount: stWht,
      SalesTaxChargedAmount: stCharged,
    }));
  }, [
    currentGrossClaimed,
    formData.RetentionRate,
    formData.IncomeTaxRate,
    formData.SalesTaxRate,
    formData.SalesTaxChargedRate,
  ]);

  // Total Deductions & Net Payable
  const totalDeductions = useMemo(() => {
    const ret = Number(formData.RetentionAmount) || 0;
    const adv = Number(formData.AdvanceAmortization) || 0;
    const it = Number(formData.IncomeTaxAmount) || 0;
    const st = Number(formData.SalesTaxAmount) || 0;
    const oth = Number(formData.OtherAdjustments) || 0;
    return ret + adv + it + st + oth;
  }, [
    formData.RetentionAmount,
    formData.AdvanceAmortization,
    formData.IncomeTaxAmount,
    formData.SalesTaxAmount,
    formData.OtherAdjustments,
  ]);

  const netPayable = useMemo(() => {
    return Math.max(
      0,
      currentGrossClaimed + (Number(formData.SalesTaxChargedAmount) || 0) - totalDeductions,
    );
  }, [currentGrossClaimed, formData.SalesTaxChargedAmount, totalDeductions]);

  // File Upload Handler
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        ProgressCertificateFile: file.name,
      }));
      toast.success(`Attached "${file.name}"`);
    }
  };

  // Submit Handler
  // An authorized bill can be cancelled with a reason; the server refuses when it has receipts or is not the latest bill.
  const handleCancelBill = async () => {
    const reason = window.prompt("Reason for cancelling this running bill:");
    if (!reason || !reason.trim()) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await cancelInvoiceApi(formData.Id, reason.trim());
      if (result?.data?.IsSuccess === true) {
        toast.success(result.data.Message);
        history.push("/client-work/invoices");
      } else {
        toast.error(result?.data?.Message || "Could not cancel the Running Bill");
      }
    } catch (err) {
      toast.error("Error cancelling Client Running Bill");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBill = async (targetStatus = "Draft") => {
    if (!formData.ContractNo) {
      toast.error("Linked Client Contract is required");
      return;
    }
    if (!formData.ClientName) {
      toast.error("Project Owner (Client) is required");
      return;
    }
    if (!formData.BillingDate) {
      toast.error("Billing Date is required");
      return;
    }
    if (!formData.CumulativeGross || Number(formData.CumulativeGross) <= 0) {
      toast.error("Please enter a valid Cumulative Gross Work Done");
      return;
    }
    if (Number(formData.CumulativePercentage) > 100) {
      toast.error("Cumulative Work Done (%) cannot exceed 100%");
      return;
    }
    const contractVal = Number(formData.OriginalContractValue) || 0;
    if (contractVal > 0 && Number(formData.CumulativeGross) > contractVal) {
      toast.error(
        `Cumulative Gross cannot exceed Total Contract Value (PKR ${contractVal.toLocaleString()})`,
      );
      return;
    }
    if (Number(formData.CumulativeGross) <= (Number(formData.PreviousGross) || 0)) {
      toast.error("Cumulative Gross must be more than the previously billed amount");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        CumulativeGross: Number(formData.CumulativeGross) || 0,
        CumulativePercentage: Number(formData.CumulativePercentage) || 0,
        CurrentGross: currentGrossClaimed,
        TotalDeductions: totalDeductions,
        NetReceivable: netPayable,
        Status: targetStatus,
      };

      const result = await saveInvoiceApi({ ...payload, ContractId: Number(formData.ContractId) });
      if (result?.data?.IsSuccess === true) {
        toast.success(result.data.Message);
        history.push("/client-work/invoices");
      } else {
        toast.error(result?.data?.Message || "Error saving Client Running Bill");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving Client Running Bill");
    } finally {
      setIsLoading(false);
    }
  };

  const contractOptions = useMemo(() => {
    return [
      { value: "", label: "Select Client Contract" },
      ...contractsList.map((c) => ({
        value: c.ContractNo,
        label: `${c.ContractNo} — ${c.ProjectName} (${c.ClientName})`,
      })),
    ];
  }, [contractsList]);

  return (
    <div className="vendor-screen-wrapper app_font">
      {/* ── Page Header ── */}
      <div className="col-md-12 text-left page_heading d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back to Invoices"
            onClick={() => history.push("/client-work/invoices")}
          />
          <h4 className="m-0">{isEdit ? "Edit" : "Create"} Client Running Bill (Invoice / IPC)</h4>
        </div>
        {/* <div className="d-flex align-items-center" style={{ gap: "10px" }}>
          <StatusChip
            status={formData.Status || "Draft"}
            type={statusChipType(formData.Status || "Draft")}
          />
          <ActionButton
            text="Cancel"
            variant="cancel"
            type="button"
            onClick={() => history.push("/client-work/invoices")}
            disabled={isLoading}
          />
          <ActionButton
            text="Save as Draft"
            variant="cancel"
            type="button"
            onClick={() => handleSaveBill("Draft")}
            disabled={isLoading}
          />
          <ActionButton
            text="Authorize & Generate Invoice"
            variant="save"
            type="button"
            onClick={() => handleSaveBill("Authorized")}
            disabled={isLoading}
          />
        </div> */}
      </div>

      <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
        <fieldset disabled={!isEditable} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          {/* ── CARD 1: Bill Identification & Context ── */}
          <div className="vendor-card">
            <h5 className="vendor-card-title">Bill Identification & Context</h5>

            <div className="row mb-2">
              <InputField
                label="Bill Number:"
                name="BillNumber"
                value={formData.BillNumber || "Auto-generated"}
                disabled
                placeholder="Auto-generated on save"
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <SelectField
                label="Linked Client Contract:"
                name="ContractNo"
                value={formData.ContractNo}
                onChange={handleContractChange}
                options={contractOptions}
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              >
                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  Original Contract Value: PKR{" "}
                  {Number(formData.OriginalContractValue || 0).toLocaleString()}
                </small>
              </SelectField>

              <InputField
                label="Project Owner (Client):"
                name="ClientName"
                value={formData.ClientName}
                onChange={handleInputChange}
                placeholder="e.g. Amanat Builders & Developers (Pvt) Ltd"
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <InputField
                label="Billing Date:"
                name="BillingDate"
                type="date"
                value={formData.BillingDate}
                onChange={handleInputChange}
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <InputField
                label="Project Name:"
                name="ProjectName"
                value={formData.ProjectName}
                onChange={handleInputChange}
                placeholder="e.g. Gulberg Heights"
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
                <label className="input_label d-block m-0 mb-2">
                  Measurement / Progress Period:
                  <span className="estaric text-danger ml-1">*</span>
                </label>
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                  <input
                    type="date"
                    name="PeriodFrom"
                    value={formData.PeriodFrom}
                    onChange={handleInputChange}
                    className="input_styling m-0"
                    style={{ width: "100%", fontSize: "12px", padding: "0 6px" }}
                    required
                  />
                  <span className="text-muted font-weight-bold">to</span>
                  <input
                    type="date"
                    name="PeriodTo"
                    value={formData.PeriodTo}
                    onChange={handleInputChange}
                    className="input_styling m-0"
                    style={{ width: "100%", fontSize: "12px", padding: "0 6px" }}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── CARD 2: Progress Valuation (Cumulative & Current Gross Work) ── */}
          <div className="vendor-card">
            <h5 className="vendor-card-title">
              Progress Valuation (Cumulative & Current Gross Work)
            </h5>

            {/* Direct Input Warning / Notice Banner */}
            <div
              className="d-flex align-items-center p-2 mb-3"
              style={{
                backgroundColor: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "6px",
                color: "#92400e",
                fontSize: "12px",
              }}
            >
              <FaInfoCircle className="mr-2 flex-shrink-0" size={15} color="#d97706" />
              <span>
                <strong>Direct Input Active:</strong> Bill is based on progressive work
                certification without Joint Measurement Sheets (JMS).
              </span>
            </div>

            {/* 4 Metric Columns in Row */}
            <div className="row">
              <InputField
                label="Cumulative Work Done (%):"
                name="CumulativePercentage"
                type="number"
                step="any"
                min="0"
                max="100"
                value={formData.CumulativePercentage}
                onChange={(e) => handleCumulativePercentageChange(e.target.value)}
                placeholder="0"
                required
                stacked
                wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
              >
                <span
                  className="position-absolute text-muted font-weight-bold"
                  style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}
                >
                  %
                </span>
                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  % of Contract (PKR {Number(formData.OriginalContractValue || 0).toLocaleString()})
                </small>
              </InputField>

              <InputField
                label="Cumulative Work Done (Gross):"
                name="CumulativeGross"
                type="number"
                min="0"
                max={Number(formData.OriginalContractValue) || undefined}
                value={formData.CumulativeGross}
                onChange={(e) => handleCumulativeGrossChange(e.target.value)}
                placeholder="0"
                required
                stacked
                wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
              >
                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  Total gross work executed to this date
                </small>
              </InputField>

              <InputField
                label="Less: Previously Billed Work (Gross):"
                name="PreviousGross"
                type="number"
                value={formData.PreviousGross}
                readOnly
                required
                stacked
                wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
              >
                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  Taken from the contract&apos;s latest authorized bill
                </small>
              </InputField>

              <InputField
                label="Gross Work Done (Current):"
                name="CurrentGrossClaimed"
                value={`PKR ${currentGrossClaimed.toLocaleString()}`}
                disabled
                stacked
                wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
                style={{
                  backgroundColor: "#f1f5f9",
                  border: "1.5px solid #cbd5e1",
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "var(--color-primary, #0b6462)",
                }}
              >
                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  Based on: Cumulative - Previous
                </small>
              </InputField>
            </div>
          </div>

          {/* ── CARD 3: Deductions & Adjustments Stack ── */}
          <div className="vendor-card">
            <h5 className="vendor-card-title">Deductions & Adjustments Stack</h5>

            <div className="row">
              {/* Left Column: Adjustment Notes & Attachment */}
              <div className="col-md-6 pr-md-4">
                <div className="form-group mb-3">
                  <label
                    className="input_label m-0 font-weight-bold"
                    style={{ fontSize: "12.5px" }}
                  >
                    Adjustment Notes & Justifications:
                  </label>
                  <textarea
                    name="AdjustmentNotes"
                    className="input_styling w-100 mt-2"
                    rows={4}
                    style={{ height: "auto", padding: "8px 12px", resize: "vertical" }}
                    value={formData.AdjustmentNotes}
                    onChange={handleInputChange}
                    placeholder="Running Bill #4. Includes 100% completion of concrete for basement and 50% Ground Floor slab. Material escalate adjustments not claimed yet."
                  />
                </div>

                <div className="form-group mb-2">
                  <label
                    className="input_label m-0 font-weight-bold"
                    style={{ fontSize: "12.5px" }}
                  >
                    Progress Certificate Upload:
                    <span className="estaric text-danger ml-1">*</span>
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    accept=".pdf,.png,.jpg,.jpeg,.docx"
                  />
                  <div
                    className="d-flex align-items-center justify-content-between p-2 mt-2 mb-1"
                    style={{
                      border: "1px dashed #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <div
                      className="d-flex align-items-center text-truncate mr-2"
                      style={{ minWidth: 0, flex: 1 }}
                    >
                      <BsFileEarmarkPdf size={18} className="text-danger mr-1 flex-shrink-0" />
                      <span
                        style={{ fontSize: "12px", color: "#334155" }}
                        className="text-truncate"
                        title={formData.ProgressCertificateFile}
                      >
                        {formData.ProgressCertificateFile || "No document attached"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-info btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ fontSize: "11px", padding: "2px 8px" }}
                    >
                      <BsCloudUpload className="mr-1" /> Upload
                    </button>
                  </div>
                  <small className="text-muted d-block" style={{ fontSize: "10.5px" }}>
                    + Upload Client/Consultant Certified Abstract / Photo (PDF/Image, Max 25MB)
                  </small>
                </div>
              </div>

              {/* Right Column: Financial Deductions Stack */}
              <div className="col-md-6 pl-md-4 border-left">
                {/* Current Gross Claimed */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ fontWeight: 600, color: "#334155", fontSize: "13.5px" }}>
                    Current Gross Claimed:
                  </span>
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "15px" }}>
                    PKR {currentGrossClaimed.toLocaleString()}
                  </span>
                </div>

                {/* Add: Sales Tax charged on the bill (output tax, owed to the tax authority) */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                      Add: Sales Tax Charged (if any)
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                    <div className="d-flex align-items-center" style={{ width: "70px" }}>
                      <input
                        type="number"
                        step="0.1"
                        className="input_styling text-right pr-1"
                        style={{
                          width: "45px",
                          height: "28px",
                          fontSize: "12px",
                          padding: "0 4px",
                        }}
                        value={formData.SalesTaxChargedRate}
                        onChange={(e) => handleNumberChange("SalesTaxChargedRate", e.target.value)}
                      />
                      <span className="ml-1" style={{ fontSize: "11px", color: "#64748b" }}>
                        %
                      </span>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-between px-2"
                      style={{
                        width: "140px",
                        height: "28px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ fontSize: "10.5px", color: "#64748b" }}>PKR</span>
                      <span>{Number(formData.SalesTaxChargedAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Less: Retention Money */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                      Less: Retention Money <span className="estaric text-danger">*</span>
                    </div>
                    <small className="text-muted" style={{ fontSize: "10.5px" }}>
                      (autocomputed as {formData.RetentionRate}% of gross)
                    </small>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                    <div className="d-flex align-items-center" style={{ width: "70px" }}>
                      <input
                        type="number"
                        step="0.1"
                        className="input_styling text-right pr-1"
                        style={{
                          width: "45px",
                          height: "28px",
                          fontSize: "12px",
                          padding: "0 4px",
                        }}
                        value={formData.RetentionRate}
                        onChange={(e) => handleNumberChange("RetentionRate", e.target.value)}
                      />
                      <span className="ml-1" style={{ fontSize: "11px", color: "#64748b" }}>
                        %
                      </span>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-between px-2"
                      style={{
                        width: "140px",
                        height: "28px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ fontSize: "10.5px", color: "#64748b" }}>PKR</span>
                      <span>{Number(formData.RetentionAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Less: Mobilization Advance Amortization */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                      Less: Mobilization Advance Amortization{" "}
                      <span className="estaric text-danger">*</span>
                    </div>
                    <small className="text-muted" style={{ fontSize: "10.5px" }}>
                      (deductible as per contract)
                    </small>
                  </div>
                  <div
                    className="d-flex align-items-center"
                    style={{ width: "216px", justifyContent: "flex-end" }}
                  >
                    <div
                      className="d-flex align-items-center px-1"
                      style={{
                        width: "140px",
                        height: "28px",
                        backgroundColor: "#fff",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                      }}
                    >
                      <span className="text-muted mr-1" style={{ fontSize: "10.5px" }}>
                        PKR
                      </span>
                      <input
                        type="number"
                        className="border-0 w-100 text-right pr-1 font-weight-bold"
                        style={{ outline: "none", fontSize: "12px" }}
                        value={formData.AdvanceAmortization}
                        onChange={(e) => handleNumberChange("AdvanceAmortization", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Less: Income Tax WHT */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                      Less: Income Tax WHT (Active Filer)
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                    <div className="d-flex align-items-center" style={{ width: "70px" }}>
                      <input
                        type="number"
                        step="0.1"
                        className="input_styling text-right pr-1"
                        style={{
                          width: "45px",
                          height: "28px",
                          fontSize: "12px",
                          padding: "0 4px",
                        }}
                        value={formData.IncomeTaxRate}
                        onChange={(e) => handleNumberChange("IncomeTaxRate", e.target.value)}
                      />
                      <span className="ml-1" style={{ fontSize: "11px", color: "#64748b" }}>
                        %
                      </span>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-between px-2"
                      style={{
                        width: "140px",
                        height: "28px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ fontSize: "10.5px", color: "#64748b" }}>PKR</span>
                      <span>{Number(formData.IncomeTaxAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Less: Sales Tax WHT */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                      Less: Sales Tax WHT (if any)
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                    <div className="d-flex align-items-center" style={{ width: "70px" }}>
                      <input
                        type="number"
                        step="0.1"
                        className="input_styling text-right pr-1"
                        style={{
                          width: "45px",
                          height: "28px",
                          fontSize: "12px",
                          padding: "0 4px",
                        }}
                        value={formData.SalesTaxRate}
                        onChange={(e) => handleNumberChange("SalesTaxRate", e.target.value)}
                      />
                      <span className="ml-1" style={{ fontSize: "11px", color: "#64748b" }}>
                        %
                      </span>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-between px-2"
                      style={{
                        width: "140px",
                        height: "28px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ fontSize: "10.5px", color: "#64748b" }}>PKR</span>
                      <span>{Number(formData.SalesTaxAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Other Adjustments / Deductions */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                      Other Adjustments / Deductions
                    </div>
                    <small
                      className="text-info cur_sor"
                      style={{ fontSize: "10.5px", cursor: "pointer", textDecoration: "underline" }}
                      onClick={() =>
                        toast.info(
                          "Additional deduction heads (e.g. testing charges, penalties, site electricity)",
                        )
                      }
                    >
                      Add Adjustment Details
                    </small>
                  </div>
                  <div
                    className="d-flex align-items-center"
                    style={{ width: "216px", justifyContent: "flex-end" }}
                  >
                    <div
                      className="d-flex align-items-center px-1"
                      style={{
                        width: "140px",
                        height: "28px",
                        backgroundColor: "#fff",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                      }}
                    >
                      <span className="text-muted mr-1" style={{ fontSize: "10.5px" }}>
                        PKR
                      </span>
                      <input
                        type="number"
                        className="border-0 w-100 text-right pr-1 font-weight-bold"
                        style={{ outline: "none", fontSize: "12px" }}
                        value={formData.OtherAdjustments}
                        onChange={(e) => handleNumberChange("OtherAdjustments", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Total Deductions Summary Row */}
                <div
                  className="d-flex justify-content-between align-items-center py-2 px-3 mb-3"
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                    Total Deductions & Recoveries:
                  </span>
                  <span style={{ fontSize: "13px", color: "#dc2626", fontWeight: 700 }}>
                    - PKR {totalDeductions.toLocaleString()}
                  </span>
                </div>

                {/* Net Payable Banner */}
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    backgroundColor: "#ecfdf5",
                    border: "1.5px solid #a7f3d0",
                    borderRadius: "8px",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "13px", color: "#065f46" }}>
                      Net Payable (Receivable from Client):
                    </div>
                    <small style={{ color: "#047857", fontSize: "11px" }}>
                      Gross Claimed minus all progressive withholdings
                    </small>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: "18px", color: "#047857" }}>
                    PKR {netPayable.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </fieldset>

        {/* ── Bottom Actions Bar ── */}
        <div className="col-12 p-0">
          <div className="float-right mt-2 mb-4 d-flex align-items-center" style={{ gap: "10px" }}>
            <ActionButton
              text="Cancel"
              variant="cancel"
              type="button"
              onClick={() => history.push("/client-work/invoices")}
              disabled={isLoading}
            />
            {isEditable && (
              <>
                <ActionButton
                  text="Save as Draft"
                  variant="cancel"
                  type="button"
                  onClick={() => handleSaveBill("Draft")}
                  disabled={isLoading}
                />
                <ActionButton
                  text="Authorize & Generate Invoice"
                  variant="save"
                  type="button"
                  onClick={() => handleSaveBill("Authorized")}
                  disabled={isLoading}
                />
              </>
            )}
            {isEdit && formData.StoredStatus === "Authorized" && (
              <ActionButton
                text="Cancel Bill"
                variant="cancel"
                type="button"
                style={{ color: "#dc3545", borderColor: "#dc3545" }}
                onClick={handleCancelBill}
                disabled={isLoading}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
